import { useState, useEffect } from 'react';
import { ProductSelectedInfo } from '../types/product.types';
import { IConfigPrice } from '../types/product/detail.types';
import { formatPrice, formatOldPrice } from '../utils/price.utils';
import { IImage } from 'components/Catalog/Interfaces/IImage';

export interface UseProductDataReturn {
    selectedProductInformation: ProductSelectedInfo;
    setSelectedProductInformation: (info: ProductSelectedInfo) => void;
    price: IConfigPrice;
    setPrice: (price: IConfigPrice) => void;
    oldPrice: string | undefined;
    setOldPrice: (oldPrice: string | undefined) => void;
    images: IImage[] | undefined;
    setImages: (images: IImage[] | undefined) => void;
    initialGalleryHtml: string | undefined;
    error: string | undefined;
    setError: (error: string | undefined) => void;
    success: string | undefined;
    setSuccess: (success: string | undefined) => void;
}

export const useProductData = (
    initialPrice: IConfigPrice,
    initialOldPrice: string | undefined,
    initialGalleryDom: string | undefined,
    priceFormat: string,
): UseProductDataReturn => {
    const [selectedProductInformation, setSelectedProductInformation] = useState<ProductSelectedInfo>({
        isAvailable: true,
    });

    const [price, setPrice] = useState<IConfigPrice>(initialPrice);
    const [oldPrice, setOldPrice] = useState<string | undefined>(initialOldPrice);
    const [images, setImages] = useState<IImage[]>();
    const [initialGalleryHtml] = useState<string | undefined>(initialGalleryDom);
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();

    // Handle price updates when selected product information changes
    useEffect(() => {
        if (selectedProductInformation?.prices) {
            const prices = selectedProductInformation.prices;
            setPrice((prevPrice) => ({
                value: prices.finalPrice.amount,
                label: prevPrice.label,
                valueLabel: formatPrice(prices.finalPrice.amount, priceFormat),
            }));

            if (prices.oldPrice) {
                setOldPrice(formatOldPrice(prices.oldPrice.amount, prices.finalPrice.amount, priceFormat));
            } else {
                setOldPrice(undefined);
            }
        }
    }, [selectedProductInformation, priceFormat]);

    // Handle images updates when product configuration changes
    useEffect(() => {
        if (selectedProductInformation?.images) {
            setImages(selectedProductInformation.images);
        }
    }, [selectedProductInformation?.images]);

    return {
        selectedProductInformation,
        setSelectedProductInformation,
        price,
        setPrice,
        oldPrice,
        setOldPrice,
        images,
        setImages,
        initialGalleryHtml,
        error,
        setError,
        success,
        setSuccess,
    };
};
